import { Box, Modal, TextField, IconButton, Button, Stack, Typography } from "@mui/material";
import { useEffect, useState, useRef } from "react";
import { Delete, Add } from "@mui/icons-material";

const style = {
    position: "absolute" as const,
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: "500px",
    bgcolor: "background.paper",
    boxShadow: 24,
    borderRadius: "12px",
    p: 4,
};

interface select {
    id: number | null;
    name: string;
    icon?: string
}

interface ComplexFunctionModalProps {
    //token: string;
    open: boolean;
    onClose: () => void;
    list: select[];
    setList: (list: select[]) => void;
}

const ComplexFunctionModal = ({ open, onClose, list, setList }: ComplexFunctionModalProps) => {
    const [functions, setFunctions] = useState<select[]>([]);
    const [changedFunctions, setChangedFunctions] = useState<select[]>([]);
    const [deletedIds, setDeletedIds] = useState<number[]>([]);
    const [activeIndex, setActiveIndex] = useState<number | null>(null);
    const messagesEndRef = useRef<null | HTMLDivElement>(null)

    useEffect(() => {
        setChangedFunctions([]);
        setFunctions(list || []);
        setDeletedIds([]);
    }, [list]);

    const handleChange = (index: number, value: string) => {
        const updated = [...functions];
        updated[index] = { ...updated[index], name: value };

      setChangedFunctions(prev => {

        const exists = prev.some(item => item.id === functions[index].id);

        if (exists) {
          return prev.map(item =>
            item.id === functions[index].id ? { ...item, name: value } : item
          );
        } else {
          return [...prev, updated[index]];
        }
      });

        setFunctions(updated);
        setActiveIndex(index);
    };

    const handleAdd = () => {
        if (functions.some(f => f.name.trim() === "")) return;
        setFunctions([...functions, { id: null, name: "" }]);
        setTimeout(() => {
            messagesEndRef.current?.scrollIntoView({ behavior: "smooth" })
        }, 300);
    };

    const markForDelete = (index: number) => {
        const item = functions[index];
        if (item.id != null) {
            setDeletedIds(prev => [...prev, item.id as number]);
        }
        setFunctions(prev => prev.filter((_, i) => i !== index));
    };



    const hasChanges = () => {
        if (deletedIds.length > 0) return true;
        if (functions.length !== list.length) return true;
        return functions.some((f, i) => f.name.trim() !== (list[i]?.name?.trim() || ""));
    };

    const hasErrorsOrNoChanges = () => {
        const trimmedNames = functions.map(f => f.name.trim());
        const hasEmpty = trimmedNames.some(name => name === "");
        const hasDuplicates = trimmedNames.some((name, i) =>
            trimmedNames.indexOf(name) !== i && name !== ""
        );
        return !hasChanges() || hasEmpty || hasDuplicates;
    };

    const handleOnClose = () => {
        setChangedFunctions([]);
        setFunctions(list || []);
        onClose()
    }

    const handleSave = async () => {
        const newFunctions = functions.filter(f => f.name.trim() !== "" && f.id === null);
        const uniqueDeletedIds = Array.from(new Set(deletedIds));

        try {
            const defaultHeadersParts = {
                Accept: 'application/json'};
            const defaultRequestParts = {method: 'GET', headers: {...defaultHeadersParts}};
            const deletePromises = uniqueDeletedIds.map(id =>
                fetch(`/api/v0/complex-function/${id}`, {
                    ...defaultRequestParts,
                    method: "DELETE",
                    credentials: 'include',
                }).then(res => {
                    if (!res.ok && res.status !== 404) {
                        throw new Error(`Errore nella cancellazione ID ${id}`);
                    }
                })
            );
            const updatePromises = changedFunctions.map(s =>
                fetch(`/api/v0/complex-function/${s.id}`, {
                    method: "PATCH",
                    headers: {
                        ...defaultHeadersParts,
                        "Content-Type": "application/json",
                    },
                    body: JSON.stringify(s),
                    credentials: 'include',
                }).then(res => {
                    if (!res.ok && res.status !== 404) {
                        throw new Error(`Errore nella cancellazione ID ${s.id}`);
                    }
                })
            );
            const createPromises = newFunctions.map(func =>
                fetch("/api/v0/complex-functions", {
                    method: "POST",
                    headers: {
                        ...defaultHeadersParts,
                        "Content-Type": "application/json",
                    },
                    body: JSON.stringify(func),
                    credentials: 'include',
                }).then(res => {
                    if (!res.ok) throw new Error(`Errore nel salvataggio funzione: ${func.name}`);
                })
            );

            await Promise.all([...deletePromises, ...updatePromises, ...createPromises]);
            const response = await fetch("/api/v0/complex-functions", {
                method: "GET",
                headers: defaultHeadersParts,
                credentials: 'include',
            });

            if (!response.ok) {
                throw new Error("Errore nel recupero delle funzioni aggiornate.");
            }

            const updatedFunctions = await response.json();
            setList(updatedFunctions);

            console.log("Tutte le modifiche sono state salvate con successo.");
            handleOnClose();
        } catch (error) {
            console.error("Errore nel salvataggio:", error);
        }
    };

    return (
        <Modal open={open} onClose={handleOnClose}>
            <Box sx={style}>

                <div className="row">
                    <div className="col text-start">
                        <h5 className="modal-title"> Gestisci lista funzioni complesse</h5>
                    </div>
                    <div className="col text-end">
                        <button type="button" className="btn-close" onClick={handleOnClose}></button>
                    </div>
                </div>

                <Stack spacing={2} sx={{ overflowY: 'auto', maxHeight: 'calc(31vh - 40px)' }}>
                    {functions.map((func, index) => {
                        const nameTrimmed = func.name.trim();
                        const isDuplicate =
                            functions.filter((f, i) => f.name.trim() === nameTrimmed && i !== index).length > 0;
                        const showError = activeIndex === index && (nameTrimmed === "" || isDuplicate);

                        return (
                            <Box key={`${func.id ?? `new-${index}`}`} display="flex" alignItems="center" gap={1}>
                                <TextField
                                    fullWidth
                                    value={func.name}
                                    onChange={(e) => handleChange(index, e.target.value)}
                                    variant="outlined"
                                    size="small"
                                    error={showError}
                                    helperText={
                                        showError
                                            ? nameTrimmed === ""
                                                ? "Il nome non può essere vuoto"
                                                : "Nome duplicato"
                                            : ""
                                    }
                                />
                                <IconButton onClick={() => markForDelete(index)} color="error">
                                    <Delete />
                                </IconButton>
                            </Box>
                        );
                    })}
                    <div ref={messagesEndRef} />
                </Stack>
                <Box display="flex" justifyContent="center" mt={2}>
                    <button className="btn btn-light" onClick={handleAdd}>
                        Inserisci nuovo <Add fontSize="small" />
                    </button>
                </Box>

                <Box mt={4} display="flex" justifyContent="flex-end" gap={2}>
                    <button className="btn btn-light" onClick={handleOnClose}>
                        Annulla
                    </button>
                    <button
                        className="btn btn-success"
                        onClick={handleSave}
                        disabled={hasErrorsOrNoChanges()}
                    >
                        Salva
                    </button>
                </Box>
            </Box>
        </Modal>
    );
};

export default ComplexFunctionModal;
